export type IntlLang = "hu" | "en";

export type Intl2 = {
  lang: IntlLang;
  locale: string;
  number(value: number, maximumFractionDigits?: number): string;
  integer(value: number): string;
  percent(ratio: number): string;
  length(meters: number): string;
  area(squareMeters: number): string;
  date(value: Date | string | number | null): string;
  dateTime(value: Date | string | number | null): string;
  plural(count: number, one: string, other: string): string;
  list(items: string[]): string;
};

const locales: Record<IntlLang, string> = { hu: "hu-HU", en: "en-GB" };

const formatterCache = new Map<string, Intl.NumberFormat>();

function numberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(key);

  if (formatter === undefined) {
    formatter = new Intl.NumberFormat(locale, options);
    formatterCache.set(key, formatter);
  }

  return formatter;
}

function toDate(value: Date | string | number | null): Date | null {
  if (value === null) return null;

  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function langOf(langId: number): IntlLang {
  return langId === 2 ? "en" : "hu";
}

export function createIntl(lang: IntlLang): Intl2 {
  const locale = locales[lang];
  const dateFormat = new Intl.DateTimeFormat(locale, { year: "numeric", month: "2-digit", day: "2-digit" });
  const dateTimeFormat = new Intl.DateTimeFormat(locale, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  const listFormat = typeof Intl.ListFormat === "function" ? new Intl.ListFormat(locale, { style: "short", type: "conjunction" }) : null;

  const number = (value: number, maximumFractionDigits = 2): string => numberFormat(locale, { maximumFractionDigits }).format(value);

  return {
    lang,
    locale,
    number,
    integer: value => numberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(value)),
    percent: ratio => `${numberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(ratio * 100))}%`,
    length: meters => {
      if (!Number.isFinite(meters)) return "";
      if (Math.abs(meters) >= 1000) return `${number(meters / 1000, 2)} km`;
      if (Math.abs(meters) >= 100) return `${number(meters, 0)} m`;

      return `${number(meters, 1)} m`;
    },
    area: squareMeters => {
      if (!Number.isFinite(squareMeters)) return "";
      if (Math.abs(squareMeters) >= 1000000) return `${number(squareMeters / 1000000, 2)} km²`;
      if (Math.abs(squareMeters) >= 10000) return `${number(squareMeters / 10000, 2)} ha`;

      return `${number(squareMeters, 0)} m²`;
    },
    date: value => {
      const date = toDate(value);

      return date === null ? "" : dateFormat.format(date);
    },
    dateTime: value => {
      const date = toDate(value);

      return date === null ? "" : dateTimeFormat.format(date);
    },
    plural: (count, one, other) => (lang === "en" ? (Math.abs(count) === 1 ? one : other) : one),
    list: items => (listFormat === null ? items.join(", ") : listFormat.format(items))
  };
}

const intlCache: Partial<Record<IntlLang, Intl2>> = {};

export function intlFor(lang: IntlLang): Intl2 {
  let value = intlCache[lang];

  if (value === undefined) {
    value = createIntl(lang);
    intlCache[lang] = value;
  }

  return value;
}
